import { Component, inject, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { BaseComponent } from "components/base/base.component";
import { NewStarRatingConvivaComponent } from 'components/star-rating/new/new-star-rating.component';
import { lastValueFrom } from "rxjs";
import { JornadaPsicologo, JornadaPsicologoAtividade } from "src/app/shared/models/psicologo/JornadaPsicologo";
import { PsicologoUtilsService } from "src/app/shared/services/psicologo-utils.service";
import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { convivaCommonImports } from 'src/app/shared/ui/conviva-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';

type AvaliacaoGroup = FormGroup<{
  estrelas: FormControl<number>;
  // justificativaAvaliacao: FormControl<string>;
  codigoAtividade: FormControl<number>;
}>;

type JustificativaAvaliacaoGroup = FormGroup<{
  justificativaAvaliacao: FormControl<string>;
}>;


@Component({
    templateUrl: './aprovacao-atividades.component.html',
    styleUrls: ['./aprovacao-atividades.component.scss'],
    standalone: true,
    imports:[angularCommonImports,convivaCommonImports,libsCommonImports,NewStarRatingConvivaComponent]
})

export class PageAprovacaoAtividadesPsicologoComponent extends BaseComponent implements OnInit {
  //Injeções
  activatedRoute = inject(ActivatedRoute);
  psicolgoUtilsService = inject(PsicologoUtilsService);
  fb = inject(FormBuilder);


  //Variaveis
  jornadaId?: number;
  atividadeId?: number;
  jornada?: JornadaPsicologo;
  descricaoJustificativaAvaliacao?: string;
  /** Todas as atividades da jornada exibidas na listagem. */
  atividadesExibidas: JornadaPsicologoAtividade[] = [];
  /** Subconjunto enviado para aprovação (validação do botão Concluir). */
  atividadesEnviadasParaAprovacao: JornadaPsicologoAtividade[] = [];

  private atualizarListasAtividades(): void {
    const atividades = this.jornada?.atividades ?? [];
    this.atividadesExibidas = atividades;
    this.atividadesEnviadasParaAprovacao = atividades.filter(
      a => this.atividadeEnviadaParaAprovacao(a)
    );
    this.atualizarValidadoresJustificativa();
  }

  private obterEstrelasAtividade(atividade: JornadaPsicologoAtividade): number {
    const indice = this.getIndiceFormulario(atividade.codigoAtividade);
    if (indice >= 0) {
      const estrelasForm = this.avaliacoesForms.at(indice)?.getRawValue()?.estrelas;
      if ((estrelasForm ?? 0) > 0) {
        return estrelasForm!;
      }
    }
    return Number(atividade.estrelasAvaliacao ?? 0);
  }

  private atualizarValidadoresJustificativa(): void {
    const controle = this.justificativaAvaliacaoForm.get('justificativaAvaliacao');
    if (!controle) {
      return;
    }
    // Justificativa é obrigatória apenas se houver atividades com 1-3 estrelas
    if (this.exigeJustificativaAvaliacao) {
      controle.setValidators([
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(500),
      ]);
    } else {
      controle.clearValidators();
    }
    controle.updateValueAndValidity({ emitEvent: false });
  }

  private obterTextoJustificativa(): string {
    return this.justificativaAvaliacaoForm.get('justificativaAvaliacao')?.value?.trim() ?? '';
  }

  atividadeEnviadaParaAprovacao(atividade: JornadaPsicologoAtividade): boolean {
    return Number(atividade.statusAprovacao ?? 0) !== 0;
  }

  getIndiceFormulario(codigoAtividade: number): number {
    return this.atividadesExibidas.findIndex(a => a.codigoAtividade === codigoAtividade);
  }

  atividadePendenteEstrelas(atividade: JornadaPsicologoAtividade): boolean {
    return this.atividadeEnviadaParaAprovacao(atividade) && (atividade.estrelasAvaliacao ?? 0) <= 0;
  }

  get atividadesPendentesAvaliacao(): JornadaPsicologoAtividade[] {
    return this.atividadesEnviadasParaAprovacao.filter(a => this.atividadePendenteEstrelas(a));
  }

  get titulosAtividadesPendentes(): string {
    return this.atividadesPendentesAvaliacao.map(a => a.titulo).join(', ');
  }

  /** Todas as atividades enviadas para aprovação possuem nota (estrelas) registrada. */
  get todasAtividadesComEstrelas(): boolean {
    const atividades = this.atividadesEnviadasParaAprovacao;
    if (!atividades.length) {
      return false;
    }
    return atividades.every(a => this.obterEstrelasAtividade(a) > 0);
  }

  /** Alguma atividade foi avaliada com 1 a 3 estrelas (mensagem contextual). */
  get exigeJustificativaAvaliacao(): boolean {
    return this.atividadesEnviadasParaAprovacao.some(a => {
      const estrelas = this.obterEstrelasAtividade(a);
      return estrelas >= 1 && estrelas <= 3;
    });
  }

  get justificativaPreenchida(): boolean {
    const texto = this.obterTextoJustificativa();
    // Preenchida quando: não é exigida OU (exigida e tem 10+ caracteres e form válido)
    if (!this.exigeJustificativaAvaliacao) {
      return true; // Não é obrigatória
    }
    return texto.length >= 10 && this.justificativaAvaliacaoForm.valid;
  }

  get justificativaSecaoSalva(): boolean {
    // Salva quando todas têm estrelas AND justificativa está preenchida ou não é exigida
    return this.todasAtividadesComEstrelas && this.justificativaPreenchida;
  }

  tentouConcluirSemJustificativa = false;

  /** Habilita o botão Concluir (verde) somente quando: todas as atividades têm nota registrada E 
   * (não há atividades com 1-3 estrelas OU justificativa foi preenchida corretamente) */
  get podeConcluirAvaliacao(): boolean {
    if (!this.todasAtividadesComEstrelas) {
      return false; // Primeiro critério: todas devem ter estrelas
    }
    // Se não há atividades com 1-3 (todas 4-5), botão habilitado sem justificativa
    if (!this.exigeJustificativaAvaliacao) {
      return true;
    }
    // Se há atividades com 1-3, precisa de justificativa preenchida
    return this.justificativaPreenchida;
  }

  /** Alias usado no template para estilo e rótulo do botão. */
  get todasAtividadesAprovadas(): boolean {
    return this.podeConcluirAvaliacao;
  }

  get atividadeReprovada(): boolean {
    return this.exigeJustificativaAvaliacao;
  }

  avaliacoesForms: FormArray<AvaliacaoGroup> = this.fb.array<AvaliacaoGroup>([]);
  justificativaAvaliacaoForm = this.fb.group({
    justificativaAvaliacao: this.fb.control('', [Validators.required, Validators.minLength(10), Validators.maxLength(500)])
  });

  totalHorasTrabalhadas?: Date;
  carregando = false;
  mensagemCarregando = 'Carregando...';
  codigoAtividadeSalvando?: number;
  concluindo = false;

  colunasGridTurma = [
    { title: 'Codigo Turma', field: 'codigoTurma', width: 200, hozAlign: "center", headerHozAlign: "center" },
    { title: 'Numero Classe', field: 'numeroClasse', width: 300, hozAlign: "center", headerHozAlign: "center" },
    { title: 'Tipo de Ensino', field: 'nomeTipoEnsino', width: 200, hozAlign: "center", headerHozAlign: "center" },
    { title: 'Numero Serie', field: 'numeroSerie', width: 300, hozAlign: "center", headerHozAlign: "center" },
  ]
  colunasGridOutros = [
    { title: 'Outros', field: 'outros', hozAlign: "center", headerHozAlign: "center" },
  ]
  colunasGridEquipeEscolar = [
    { title: 'Equipe Escolar ', field: 'nomeEquipeEscolar', hozAlign: "center", headerHozAlign: "center" },
  ]

  //Funções
  async ngOnInit(): Promise<void> {
    this.lerParametrosRota();
    if (!this.jornadaId) {
      this.toastr.error("ID da jornada não informado.");
      return;
    }

    this.aplicarJornadaDoEstadoNavegacao();
    if (this.atividadesExibidas.length) {
      this.montarForms();
      this.efetuaCalculoHorasTrabalhadas();
    }

    await this.executarComLoading('Carregando atividades da jornada...', async () => {
      await this.getJornadaPorId(this.jornadaId!, false);
      this.montarForms();
      this.efetuaCalculoHorasTrabalhadas();
    });
  }

  private async executarComLoading<T>(mensagem: string, acao: () => Promise<T>): Promise<T> {
    this.mensagemCarregando = mensagem;
    this.carregando = true;
    this.spinnerService.show();
    try {
      return await acao();
    } finally {
      this.carregando = false;
      this.spinnerService.hide();
    }
  }

  atividadeEstaSalvando(codigoAtividade: number): boolean {
    return this.codigoAtividadeSalvando === codigoAtividade;
  }

  private aplicarStatusAposEstrelas(atividade: JornadaPsicologoAtividade, estrelas: number): void {
    atividade.estrelasAvaliacao = estrelas;
    if (estrelas < 3) {
      atividade.statusAprovacao = 3;
    } else if (estrelas === 3) {
      atividade.statusAprovacao = 4;
    } else if (estrelas >= 4) {
      atividade.statusAprovacao = 2;
    }
  }

  private lerParametrosRota(): void {
    const params = this.activatedRoute.snapshot.queryParamMap;
    const jornada = Number(params.get('jornada'));
    const atividade = params.get('atividade');
    this.jornadaId = Number.isFinite(jornada) ? jornada : undefined;
    this.atividadeId = atividade ? Number(atividade) : undefined;
  }

  private aplicarJornadaDoEstadoNavegacao(): void {
    const state = (history.state ?? {}) as { jornada?: JornadaPsicologo };
    if (!state?.jornada?.atividades?.length) {
      return;
    }
    this.jornada = this.normalizarJornada(state.jornada);
    this.atualizarListasAtividades();
  }

  private normalizarJornada(jornada: JornadaPsicologo): JornadaPsicologo {
    const raw = jornada as JornadaPsicologo & {
      Atividades?: JornadaPsicologoAtividade[];
      atividadesJson?: string;
      AtividadesJson?: string;
    };

    let atividades = raw.atividades ?? raw.Atividades ?? [];
    if (!atividades.length) {
      const json = raw.atividadesJson ?? raw.AtividadesJson;
      if (json) {
        try {
          atividades = JSON.parse(json) as JornadaPsicologoAtividade[];
        } catch {
          atividades = [];
        }
      }
    }

    return {
      ...jornada,
      atividades: atividades.map(a => ({
        ...a,
        envolvidos: a.envolvidos ?? [],
        arquivos: a.arquivos ?? [],
      })),
    };
  }

  async getJornadaPorId(id: number, exibirToastSucesso = true) {
    const response = await lastValueFrom(this.psicologoService.getJornadaPorId(id));
    if (!response.isSucess) {
      this.toastr.error(response.message, response.title);
      return;
    }

    const jornadaApi = this.normalizarJornada(response.data);
    this.jornada = {
      ...(this.jornada ?? {}),
      ...jornadaApi,
      atividades: jornadaApi.atividades?.length
        ? jornadaApi.atividades
        : (this.jornada?.atividades ?? []),
    };

    if (!this.jornada.atividades?.length) {
      await this.carregarAtividadesFallback(id);
    }

    this.atualizarListasAtividades();

    if (!this.atividadesExibidas.length) {
      this.toastr.warning(
        'Esta jornada não possui atividades cadastradas no sistema. Não é possível realizar a aprovação.',
        'Atenção'
      );
      return;
    }

    if (exibirToastSucesso) {
      this.toastr.success("Jornada encontrada com sucesso!");
    }
  }

  private async carregarAtividadesFallback(codigoJornada: number): Promise<void> {
    const response = await lastValueFrom(
      this.psicologoService.getJornadaAtividades(codigoJornada)
    ).catch(() => null);

    if (!response?.isSucess || !response.data?.length) {
      return;
    }

    this.jornada!.atividades = response.data.map((a: JornadaPsicologoAtividade) => ({
      ...a,
      envolvidos: a.envolvidos ?? [],
      arquivos: a.arquivos ?? [],
      descricaoAcaoRealizada: a.descricaoAcaoRealizada ?? '',
      descricaoProximosPassos: a.descricaoProximosPassos ?? '',
    }));
  }

  private efetuaCalculoHorasTrabalhadas() {
    this.totalHorasTrabalhadas = this.psicolgoUtilsService.calculaTotalHorasTrabalhadas(this.jornada);
  }

  hasTipoPessoa(atividade: JornadaPsicologoAtividade, tipoPessoa: number): boolean {
    return (atividade.envolvidos ?? []).some(envolvido => envolvido.tipoPessoa === tipoPessoa);
  }

  getTipoAtividade(tipoAtividade: number): string {
    switch (tipoAtividade) {
      case 1:
        return 'Acolhimento Não Agendado';
      case 2:
        return 'Acolhimento Agendado';
      case 3:
        return 'Prevenção Não Agendado';
      case 4:
        return 'Prevenção Agendado';
      default:
        return '';

    }

  }


  async avaliarAtividade(codigoAtividade: number) {
    // Evita double-submit da mesma atividade (cliques rápidos nas estrelas).
    if (this.codigoAtividadeSalvando === codigoAtividade || this.concluindo) {
      return;
    }

    const index = this.getIndiceFormulario(codigoAtividade);
    if (index < 0) {
      return;
    }

    const g = this.avaliacoesForms.at(index);
    if (g.invalid) {
      g.markAllAsTouched();
      return;
    }

    const { estrelas } = g.getRawValue();
    const atividade = this.atividadesExibidas.find(a => a.codigoAtividade === codigoAtividade);
    if (!atividade) {
      return;
    }

    this.codigoAtividadeSalvando = codigoAtividade;
    await this.executarComLoading('Salvando avaliação da atividade...', async () => {
      const body = {
        codigoAtividade,
        estrelas,
        codigoUsuario: this.userLogado?.codigoUsuario,
        nomeUsuario: this.userLogado?.nome
      };
      const response = await lastValueFrom(this.psicologoService.setAvaliarAtividade(body))
        .catch(err => this.convivaErrors.handleResultError(err));
      if (!response?.isSucess) {
        this.toastr.error(response?.message ?? 'Não foi possível salvar a avaliação.');
        return;
      }

      this.aplicarStatusAposEstrelas(atividade, estrelas);
      this.atualizarListasAtividades();
      this.montarForms();
      this.toastr.success("Atividade avaliada com sucesso!", "Sucesso!");
    });
    this.codigoAtividadeSalvando = undefined;
  }

  private montarForms(): void {
    this.avaliacoesForms.clear();
    this.justificativaAvaliacaoForm.reset();
    this.justificativaAvaliacaoForm.markAsUntouched();

    if (!this.atividadesExibidas.length) { return; }

    for (const a of this.atividadesExibidas) {
    const estrelasInit       = a.estrelasAvaliacao      ?? 0;

    const grupo = this.fb.group({
      estrelas:              this.fb.nonNullable.control(estrelasInit, Validators.required),
      codigoAtividade:       this.fb.nonNullable.control(a.codigoAtividade)
    });

    const jaAvaliado = estrelasInit > 0; //|| justificativaInit.trim() !== ''
    if (jaAvaliado) {
      grupo.disable({ emitEvent: false });
    }

    this.avaliacoesForms.push(grupo);
    }

    let justificativaInit  = '';
    if(this.atividadesExibidas.length > 0){
      justificativaInit = this.atividadesExibidas[0].descricaoJustificativaAvaliacao ?? '';
    }
    let justificativaSecond = ''
    if(this.atividadesExibidas.length > 1){
      justificativaSecond = this.atividadesExibidas[1].descricaoJustificativaAvaliacao ?? '';
    }
    let justificativa = '';
    if(justificativaInit != justificativaSecond && this.atividadesExibidas.length > 1){
      for (const a of this.atividadesExibidas) {
        if(a.descricaoJustificativaAvaliacao != null){
          justificativa += a.descricaoJustificativaAvaliacao + ' / ';
        }
      }
      this.justificativaAvaliacaoForm.patchValue({
        justificativaAvaliacao: justificativa
      });
    }
    else{
      this.justificativaAvaliacaoForm.patchValue({
        justificativaAvaliacao: justificativaInit
      });
    }
    
    this.justificativaAvaliacaoForm.markAsUntouched();
    this.atualizarValidadoresJustificativa();
  }

  private validarAntesConcluir(): boolean {
    this.tentouConcluirSemJustificativa = true;

    if (!this.todasAtividadesComEstrelas) {
      this.toastr.warning(this.mensagemBloqueioConclusao(), 'Atenção');
      return false;
    }

    // Validar justificativa apenas se for exigida (atividades com 1-3 estrelas)
    if (this.exigeJustificativaAvaliacao) {
      const texto = this.obterTextoJustificativa();
      if (texto.length < 10 || this.justificativaAvaliacaoForm.invalid) {
        this.justificativaAvaliacaoForm.markAllAsTouched();
        this.toastr.warning(
          'É obrigatória a justificativa em caso de avaliação entre 1 e 3 estrelas (mínimo 10 caracteres).',
          'Atenção'
        );
        return false;
      }
    }

    return true;
  }

  private mensagemBloqueioConclusao(): string {
    if (!this.atividadesEnviadasParaAprovacao.length) {
      return 'Nenhuma atividade foi enviada para aprovação nesta jornada.';
    }
    if (!this.todasAtividadesComEstrelas) {
      const titulos = this.atividadesPendentesAvaliacao.map(a => a.titulo).join(', ');
      return titulos
        ? `Existem atividades pendentes de avaliação: ${titulos}.`
        : 'Existem atividades pendentes de avaliação.';
    }
    if (!this.justificativaPreenchida) {
      return this.exigeJustificativaAvaliacao
        ? 'É obrigatória a justificativa em caso de avaliação entre 1 e 3 estrelas (mínimo 10 caracteres).'
        : 'Preencha a justificativa da avaliação (mínimo 10 caracteres) antes de concluir.';
    }
    return 'Não foi possível concluir a aprovação. Verifique as avaliações.';
  }

  async avaliarJustificativaAtividade(): Promise<boolean> {
    if (!this.validarAntesConcluir()) {
      return false;
    }

    const justificativa = this.obterTextoJustificativa();

    this.concluindo = true;
    let sucesso = false;
    await this.executarComLoading('Concluindo aprovação das atividades...', async () => {
      const body = {
        codigoJornada: this.jornadaId,
        justificativaAvaliacao: justificativa,
        codigoUsuario: this.userLogado?.codigoUsuario,
        nomeUsuario: this.userLogado?.nome
      };
      const response = await lastValueFrom(this.psicologoService.setAtualizaJustificativaAvaliacao(body))
        .catch(err => this.convivaErrors.handleResultError(err));

      const aprovacaoOk = response?.isSucess === true;
      if (!aprovacaoOk) {
        this.toastr.error(
          response?.message ?? 'Não foi possível concluir a aprovação das atividades.',
          response?.title ?? 'Erro'
        );
        return;
      }

      for (const atividade of this.atividadesEnviadasParaAprovacao) {
        const estrelas = this.obterEstrelasAtividade(atividade);
        atividade.descricaoJustificativaAvaliacao = justificativa;
        if (estrelas > 0) {
          this.aplicarStatusAposEstrelas(atividade, estrelas);
        }
      }

      this.toastr.success(
        response?.message || 'Atividades aprovadas com sucesso!',
        response?.title || 'Sucesso'
      );
      sucesso = true;
    });
    this.concluindo = false;
    return sucesso;
  }

  async concluirAvaliacaoAtividade(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.concluindo || this.carregando) {
      return;
    }

    if (!this.validarAntesConcluir()) {
      return;
    }

    const success = await this.avaliarJustificativaAtividade();
    if (!success) {
      return;
    }

    await this.redirecionarAposConclusao();
  }

  private redirecionarAposConclusao(): Promise<void> {
    return new Promise(resolve => {
      setTimeout(() => {
        this.router.navigate(['/painel-atividade-psicologo']).then(() => resolve());
      }, 1200);
    });
  }

}
